import {
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Grid,
  useToast,
  Textarea,
  Checkbox,
  Box,
  Heading,
} from "@chakra-ui/react";
import {useState} from "react";
import {SOCIAL_FIELDS, updatePatient} from "./helpers/updatePatient.js";
import CustomSelectField from "./Fields/CustomSelectField.jsx";

export default function SocialHistory({state, dispatch}) {
  const [patient, setPatient] = useState(state.patient);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const usageOptions = [
    "Never",
    "Have Tried",
    "Occasionally",
    "Sometimes",
    "Frequently",
    "Daily",
    "Multiple Times Daily",
  ];

  return (
    <Box w="full" px={0} py={2}>
      <form
        onSubmit={async (e) => {
          try {
            setIsLoading(true);
            e.preventDefault();
            await updatePatient({patientData: patient, fields: SOCIAL_FIELDS, state});
            dispatch({type: "UPDATE_PATIENT", payload: patient});
            toast({
              title: "Saved",
              description: "Social history updated successfully",
              status: "success",
              duration: 4000,
              isClosable: true,
            });
          } catch (error) {
            toast({
              title: "Information Not Updated",
              description: error?.response?.data || error.message,
              status: "error",
              isClosable: true,
            });
          } finally {
            setIsLoading(false);
          }
        }}
      >
        <Box
          bg="gray.50"
          borderRadius="12px"
          px={{base: 4, md: 6}}
          py={{base: 4, md: 6}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Employment & Education
          </Heading>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            <FormControl id="occupation">
              <FormLabel>Occupation</FormLabel>
              <Input
                size="sm"
                variant="outline"
                borderRadius="md"
                type="text"
                placeholder="Software Engineer"
                value={patient.occupation || ""}
                onChange={(e) =>
                  setPatient({...patient, occupation: e.target.value})
                }
              />
            </FormControl>

            <FormControl id="employerName">
              <FormLabel>Employer Name</FormLabel>
              <Input
                size="sm"
                variant="outline"
                borderRadius="md"
                type="text"
                placeholder="Google"
                value={patient.employerName || ""}
                onChange={(e) =>
                  setPatient({...patient, employerName: e.target.value})
                }
              />
            </FormControl>

            <CustomSelectField
              id="economicStatus"
              label="Economic Status"
              value={patient.economicStatus || ""}
              onChange={(e) =>
                setPatient({...patient, economicStatus: e.target.value})
              }
              options={[
                "Employed",
                "Unemployed",
                "Student",
                "Retired",
                "On Social Assist/Other",
              ].map((option) => ({label: option, value: option}))}
            />

            <CustomSelectField
              id="educationLevel"
              label="Education Level"
              value={patient.educationLevel || ""}
              onChange={(e) =>
                setPatient({...patient, educationLevel: e.target.value})
              }
              options={[
                "Elementary",
                "High School",
                "College",
                "Vocational/Trade School",
                "Associate Degree (1-2yrs)",
                "Bachelor Degree (3-5yrs)",
                "Masters",
                "Doctorate (PhD)",
              ].map((option) => ({label: option, value: option}))}
            />
          </Grid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          px={{base: 4, md: 6}}
          py={{base: 4, md: 6}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Substance Use
          </Heading>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            <CustomSelectField
              id="tobacco"
              label="Tobacco Use"
              value={patient.tobacco || ""}
              onChange={(e) =>
                setPatient({...patient, tobacco: e.target.value})
              }
              options={usageOptions.map((option) => ({
                label: option,
                value: option,
              }))}
            />

            <CustomSelectField
              id="alcohol"
              label="Alcohol Use"
              value={patient.alcohol || ""}
              onChange={(e) =>
                setPatient({...patient, alcohol: e.target.value})
              }
              options={usageOptions.map((option) => ({
                label: option,
                value: option,
              }))}
            />

            <CustomSelectField
              id="caffeine"
              label="Caffeine Use"
              value={patient.caffeine || ""}
              onChange={(e) =>
                setPatient({...patient, caffeine: e.target.value})
              }
              options={usageOptions.map((option) => ({
                label: option,
                value: option,
              }))}
            />

            <CustomSelectField
              id="cannabis"
              label="Cannabis Use"
              value={patient.cannabis || ""}
              onChange={(e) =>
                setPatient({...patient, cannabis: e.target.value})
              }
              options={usageOptions.map((option) => ({
                label: option,
                value: option,
              }))}
            />
          </Grid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          px={{base: 4, md: 6}}
          py={{base: 4, md: 6}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Lifestyle
          </Heading>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            <CustomSelectField
              id="maritalStatus"
              label="Marital Status"
              value={patient.maritalStatus || ""}
              onChange={(e) =>
                setPatient({...patient, maritalStatus: e.target.value})
              }
              options={[
                "Single",
                "Married",
                "Common-Law",
                "Separated/Divorced",
                "Widowed",
              ].map((option) => ({label: option, value: option}))}
            />

            <CustomSelectField
              id="sexualOrientation"
              label="Sexual Orientation"
              value={patient.sexualOrientation || ""}
              onChange={(e) =>
                setPatient({...patient, sexualOrientation: e.target.value})
              }
              options={[
                "Androgynous",
                "Asexual",
                "Bisexual",
                "Gay",
                "Heterosexual",
                "Intersex",
                "Lesbian",
                "Pansexual",
                "Queer",
                "Questioning",
                "Transgender",
                "Two-Spirit (2S)",
                "Prefer not to say",
              ].map((option) => ({label: option, value: option}))}
            />

            <CustomSelectField
              id="exerciseFrequency"
              label="Exercise Frequency"
              value={patient.exerciseFrequency || ""}
              onChange={(e) =>
                setPatient({...patient, exerciseFrequency: e.target.value})
              }
              options={[
                "Never",
                "Couple times a year",
                "Couple times a month",
                "Couple times a week",
                "Daily",
              ].map((option) => ({label: option, value: option}))}
            />
          </Grid>
        </Box>

        <Box
          bg="gray.50"
          borderRadius="12px"
          px={{base: 4, md: 6}}
          py={{base: 4, md: 6}}
          mb={4}
        >
          <Heading as="h3" size="sm" mb={3} color="gray.800">
            Additional Information
          </Heading>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            }}
            gap={3}
          >
            <Box>
              <FormControl>
                <Checkbox
                  isChecked={!!patient.dietaryRestrictions}
                  onChange={(e) =>
                    setPatient({
                      ...patient,
                      dietaryRestrictions: e.target.checked,
                    })
                  }
                >
                  Dietary Restrictions
                </Checkbox>
              </FormControl>
              <FormControl mt="2">
                <Input
                  size="sm"
                  variant="flushed"
                  isDisabled={!patient.dietaryRestrictions}
                  type="text"
                  placeholder={
                    !patient.dietaryRestrictions
                      ? "No restrictions"
                      : "Vegan and gluten-free"
                  }
                  value={
                    patient.dietaryRestrictions &&
                    patient.dietaryRestrictions !== true
                      ? patient.dietaryRestrictions
                      : ""
                  }
                  onChange={(e) =>
                    setPatient({
                      ...patient,
                      dietaryRestrictions: e.target.value,
                    })
                  }
                />
              </FormControl>
            </Box>

            <Box>
              <FormControl>
                <Checkbox
                  isChecked={!!patient.psychedelics}
                  onChange={(e) =>
                    setPatient({...patient, psychedelics: e.target.checked})
                  }
                >
                  Consume(d) Psychedelics?
                </Checkbox>
              </FormControl>
              {patient.psychedelics && (
                <FormControl mt="2">
                  <Textarea
                    size="sm"
                    variant="flushed"
                    placeholder="Went on a psilocibin macrodose trip in 2019."
                    value={
                      patient.psychedelics && patient.psychedelics !== true
                        ? patient.psychedelics
                        : ""
                    }
                    onChange={(e) =>
                      setPatient({...patient, psychedelics: e.target.value})
                    }
                  />
                </FormControl>
              )}
            </Box>

            <Box>
              <FormControl>
                <Checkbox
                  isChecked={!!patient.hardDrugs}
                  onChange={(e) =>
                    setPatient({...patient, hardDrugs: e.target.checked})
                  }
                >
                  Consume(d) Illicit Drugs?
                </Checkbox>
              </FormControl>
              {patient.hardDrugs && (
                <FormControl mt="2">
                  <Textarea
                    size="sm"
                    variant="flushed"
                    placeholder="Have taken fentanyl in the past. 1 time occurence."
                    value={
                      patient.hardDrugs && patient.hardDrugs !== true
                        ? patient.hardDrugs
                        : ""
                    }
                    onChange={(e) =>
                      setPatient({...patient, hardDrugs: e.target.value})
                    }
                  />
                </FormControl>
              )}
            </Box>

            <Box>
              <FormControl>
                <Checkbox
                  isChecked={!!patient.military}
                  onChange={(e) =>
                    setPatient({...patient, military: e.target.checked})
                  }
                >
                  Served in Military?
                </Checkbox>
              </FormControl>
              {patient.military && (
                <FormControl mt="2">
                  <Textarea
                    size="sm"
                    variant="flushed"
                    placeholder="One deployment to Afghanistan in 2006"
                    value={
                      patient.military && patient.military !== true
                        ? patient.military
                        : ""
                    }
                    onChange={(e) =>
                      setPatient({...patient, military: e.target.value})
                    }
                  />
                </FormControl>
              )}
            </Box>

            <Box>
              <FormControl>
                <Checkbox
                  isChecked={!!patient.disability}
                  onChange={(e) =>
                    setPatient({...patient, disability: e.target.checked})
                  }
                >
                  Disability/Impairment(s)?
                </Checkbox>
              </FormControl>
              {patient.disability && (
                <FormControl mt="2">
                  <Textarea
                    size="sm"
                    variant="flushed"
                    placeholder="Have spinal cord injury preventing movement."
                    value={
                      patient.disability && patient.disability !== true
                        ? patient.disability
                        : ""
                    }
                    onChange={(e) =>
                      setPatient({...patient, disability: e.target.value})
                    }
                  />
                </FormControl>
              )}
            </Box>
          </Grid>
        </Box>

        <Button
          isLoading={isLoading}
          variant="outline"
          borderColor="gray.300"
          bg="white"
          _hover={{bg: "#f8f9fa"}}
          type="submit"
          my={3}
          px="8"
          size="sm"
          alignSelf="center"
          display="block"
          mx="auto"
        >
          Save
        </Button>
      </form>
    </Box>
  );
}
